import { sanitizeHtml } from "utils/sanitizeHtml";
import { FC } from "react";
import { useData } from "data/data-provider";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useInView } from "designs/DesignB/hooks/useInView";
import { sectionPaddingY } from "utils/sectionSpacing";

export interface HomeAboutUsProps {
  className?: string;
}

/**
 * About Us section for the Design B home page.
 *
 * Pulls heading + description + image from the FMS `about-us` section. Lays
 * out as image / copy on alternating sides (image on the right by default,
 * caption stat chips on top of the image). Designed to feel distinct from
 * Design A's About hero — this is a content block aimed at landing pages,
 * not a top-of-page hero.
 */
const HomeAboutUs: FC<HomeAboutUsProps> = ({ className = "" }) => {
  const { aboutUs, companyDetails } = useData();
  const navigate = useNavigate();

  const heading =
    aboutUs?.heading?.trim() ||
    `About ${companyDetails?.company_name?.trim() || "Us"}`;
  const description = aboutUs?.description?.trim() || "";
  const imageUrl =
    aboutUs?.image_url ||
    "https://images.unsplash.com/photo-1525609004556-c46c7d6cf023?auto=format&fit=crop&w=1600&q=80";

  // 1NOW About Us layout variants (chosen in the FMS Edit Website panel,
  // persisted on Section.alignment):
  //   "left"   → image on the left,  copy on the right
  //   "center" → single column,      image on top, copy centered below
  //   "right"  → image on the right, copy on the left  (current visual,
  //                                                    used as fallback)
  // Default to "right" so tenants who haven't set alignment yet see the
  // exact same render as before the picker existed.
  const alignment: "left" | "center" | "right" =
    aboutUs?.alignment === "left" || aboutUs?.alignment === "center"
      ? aboutUs.alignment
      : "right";

  // Stats + badge come from the about-us section's keypoints, split by
  // `keypoint_type`. Convention:
  //   keypoint_type="stat"  → heading=value (e.g. "12+"), description=label
  //   keypoint_type="badge" → heading=label (e.g. "Trusted Since 2013")
  // Anything without a recognized type is ignored. Both areas hide when
  // empty so the layout stays clean if a tenant hasn't configured them yet.
  const aboutKeypoints: any[] = aboutUs?.keypoints || [];
  const stats = aboutKeypoints
    .filter((kp) => kp?.keypoint_type === "stat" && (kp?.heading || kp?.description))
    .slice(0, 4);
  const badge = aboutKeypoints.find(
    (kp) => kp?.keypoint_type === "badge" && kp?.heading
  );

  const headerEntrance = useInView<HTMLDivElement>();
  const imageEntrance = useInView<HTMLDivElement>();

  // Alignment-derived layout classes. Mobile (<lg) is always single
  // column regardless of alignment — these only affect lg+ viewports.
  const isCentered = alignment === "center";
  const isImageLeft = alignment === "left";
  // Copy entrance direction follows the side the copy sits on:
  //   right-aligned image / left-side copy  → copy slides in from the left
  //   left-aligned  image / right-side copy → copy slides in from the right
  //   centered                              → copy fades up
  const copyHiddenTransform = isCentered
    ? "opacity-0 translate-y-8"
    : isImageLeft
      ? "opacity-0 translate-x-8"
      : "opacity-0 -translate-x-8";
  const imageHiddenTransform = isCentered
    ? "opacity-0 translate-y-8"
    : isImageLeft
      ? "opacity-0 -translate-x-8"
      : "opacity-0 translate-x-8";

  return (
    <section
      className={`relative ${sectionPaddingY(
        aboutUs?.vertical_spacing,
        "py-10 md:py-16 lg:py-20"
      )} ${className}`}
    >
      <div
        className={
          isCentered
            ? // Centered mode: full-width single column. No max-w cap on the
              // container — image + copy take the full row width allotted by
              // the parent. Headline / description / CTA still appear
              // centered horizontally via `text-center` + `items-center`.
              "flex flex-col items-center gap-8 md:gap-12 lg:gap-16 w-full text-center"
            : "grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center"
        }
      >
        {/* Copy column.
            Order classes:
              centered    → image rendered FIRST (above), copy second (below)
              image-left  → copy lg:order-2 (right side)
              image-right → copy lg:order-1 (left side, default behavior) */}
        <div
          ref={headerEntrance.ref}
          className={`${
            isCentered
              ? "order-2"
              : isImageLeft
                ? "order-2 lg:order-2"
                : "order-2 lg:order-1"
          } transform-gpu transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]
            ${
              headerEntrance.inView
                ? "opacity-100 translate-x-0 translate-y-0"
                : copyHiddenTransform
            }`}
        >
          <span className="inline-block mb-4 px-4 py-1.5 text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase rounded-full border border-primary-200/40 bg-primary-100/10 text-primary-300">
            About Us
          </span>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.1] mb-5 text-text-color
              [&_p]:m-0 [&_p]:inline [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(heading) }}
          />
          {description && (
            <div
              className="text-base sm:text-lg text-secondary-text-color leading-relaxed mb-6
                [&_p]:mb-3 last:[&_p]:mb-0
                [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                [&_strong]:font-semibold [&_em]:italic [&_u]:underline
                [&_a]:text-primary-200 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
            />
          )}

          {/* Stat chips — small numeric proof points to break up the wall of
              text. Each chip is one keypoint on the about-us section with
              keypoint_type="stat" (heading=value, description=label). When
              none are configured the row hides entirely so we don't ship
              empty placeholders. When the section is centered we let the
              chip row breathe full-width below the headline (no max-w cap)
              and center it horizontally; in side-by-side layouts it caps
              at max-w-md so the chips don't sprawl across the copy column. */}
          {stats.length > 0 && (
            <div
              className={`grid gap-3 sm:gap-5 mt-7 mb-7 ${
                isCentered ? "mx-auto" : "max-w-md"
              } ${
                stats.length === 1
                  ? "grid-cols-1"
                  : stats.length === 2
                  ? "grid-cols-2"
                  : stats.length === 4
                  ? "grid-cols-2 sm:grid-cols-4"
                  : "grid-cols-3"
              }`}
            >
              {stats.map((s, i) => (
                <div
                  key={s.id ?? i}
                  className="rounded-2xl bg-secondary-50/70 border border-secondary-100 px-3 py-3 text-center"
                >
                  <div
                    className="text-xl sm:text-2xl font-extrabold text-text-color leading-none [&_p]:m-0 [&_p]:inline"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.heading || "") }}
                  />
                  <div
                    className="mt-1 text-[10px] sm:text-xs uppercase tracking-wider text-secondary-text-color font-semibold [&_p]:m-0 [&_p]:inline"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.description || "") }}
                  />
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate("/about")}
            className={`group/cta inline-flex items-center gap-2 bg-button-color text-white px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:bg-primary-200 hover:text-on-primary hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary-200/30 ${
              isCentered ? "mx-auto" : ""
            }`}
          >
            Learn more about us
            <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5" />
          </button>
        </div>

        {/* Image column.
            Order classes:
              centered    → image rendered FIRST (above the copy)
              image-left  → image lg:order-1 (left side)
              image-right → image lg:order-2 (right side, default behavior)
            Centered mode also caps the image's width so it doesn't dominate
            the centered text layout. */}
        <div
          ref={imageEntrance.ref}
          className={`${
            isCentered
              ? // Centered mode: image takes the full available width too —
                // no max-w cap. The image element inside is already
                // `w-full` so it'll fill whatever this container gives it.
                "order-1 w-full"
              : isImageLeft
                ? "order-1 lg:order-1"
                : "order-1 lg:order-2"
          } relative transform-gpu transition-all duration-1000 delay-150 ease-[cubic-bezier(0.16,1,0.3,1)]
            ${
              imageEntrance.inView
                ? "opacity-100 translate-x-0 translate-y-0"
                : imageHiddenTransform
            }`}
        >
          {/* Decorative offset blob behind the image */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -inset-4 -z-10 rounded-[2.5rem] bg-gradient-to-br from-primary-100/20 via-primary-200/10 to-transparent blur-2xl"
          />
          <div className="relative overflow-hidden rounded-3xl shadow-2xl shadow-text-color/15 ring-1 ring-text-color/10">
            <img
              src={imageUrl}
              alt={aboutUs?.heading || "About us"}
              className="w-full h-[360px] sm:h-[420px] lg:h-[520px] object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.04]"
            />
            {/* Floating badge in the corner — single about-us keypoint with
                keypoint_type="badge", heading=label. Hidden when not set. */}
            {badge && (
              <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-bg-color/90 backdrop-blur-md px-4 py-2 ring-1 ring-text-color/10 shadow-lg">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-primary-200 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-200" />
                </span>
                <span
                  className="text-[11px] font-semibold tracking-wider uppercase text-text-color [&_p]:m-0 [&_p]:inline"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(badge.heading) }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeAboutUs;
